import React, { useEffect, useState } from "react";
import styled from "styled-components";
import theme from "../../../../UI/theme";
import { ModalContainer, ModalHeader, ModalText } from "../../../panel/ModalStyles";
import Button from "../../../UiComponents/Button";
import LoadingSpinner from "../../../UiComponents/LoadingSpinner";
import serverConnection from "../../../../scripts/server/ServerConnection";
import { connectionTypes, processorTypes } from "../../../settings/venue/device/AudioSystem/processorTypes";
import InnerCard from "../../../UiComponents/containers/InnerCard";
import { updateProcessors } from "../../../../scripts/processor";


const Frame = styled.div`
    box-sizing: border-box;
    border: 3px solid ${theme.colors.darkSpace80};
    padding: 15px 30px ;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    gap 20px;
    
    
`

const ResultContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 300px;
`

const CenterContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    width: 100%;
    height: 100%;
    gap: 40px;
    text-align: center;
`

const ResultCard = styled(InnerCard)`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px 10px 20px 10px;
    width: 100%;
`

const LineResultContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 95%;
`

const RECOVERY_STAGES = {
    IDLE: 1,
    FETCHING: 2,
    PROCESSING: 3,
    UPDATING_IP: 4,
    RECOVERED: 5
}

const RECOVERY_RESULTS = {
    NONE: 0,
    SUCCESS: 1,
    EMPTY: 2,
    NO_PROCESSORS: 3,
    FAILED: 4
}

const ProcessorRecovery = ({
    hubs,
    processors,
    onClose
}) => {

    const [recoveryStage, setRecoveryStage] = useState(RECOVERY_STAGES.IDLE);
    const [recoveryResult, setRecoveryResult] = useState(RECOVERY_RESULTS.NONE);
    const [disabled, setDisabled] = useState(false);
    const [tcpProcessors, setTcpProcessors] = useState(null);
    const [fetchedDevices, setFetchedDevices] = useState(null);
    const [foundProcessors, setFoundProcessors] = useState(null);

    useEffect(() => {
        const tcpProcessorTypes = new Set(
            Object.values(processorTypes)
            .filter(p => p.connection === connectionTypes.TCP)
            .map(p => p.value)
        );
        setTcpProcessors(processors.filter(p => tcpProcessorTypes.has(p.type)));
    }, [processors]);

    useEffect(() => {
        setDisabled(recoveryStage !== RECOVERY_STAGES.IDLE || !tcpProcessors?.length);
    }, [recoveryStage, tcpProcessors]);

    function resetRecoverySettings() {
        setRecoveryStage(RECOVERY_STAGES.IDLE);
        setRecoveryResult(RECOVERY_RESULTS.NONE);
        setFetchedDevices(null);
        setFoundProcessors(null);
    }

    async function startProcessorRecovery() {
        resetRecoverySettings();
        setRecoveryStage(RECOVERY_STAGES.FETCHING);
        const scanResults = await fetchDevicesOnHubNetwork();
        
        if (!scanResults) {
            setRecoveryStage(RECOVERY_STAGES.IDLE);
            setRecoveryResult(RECOVERY_RESULTS.EMPTY);
            return;
        } else setRecoveryStage(RECOVERY_STAGES.PROCESSING);

        const allFoundDevices = filterScanResults(scanResults);
        setFetchedDevices(allFoundDevices);

        const foundProcessors = filterExistingProcessors(allFoundDevices);
        setFoundProcessors(foundProcessors);

        if (!foundProcessors.length) {
            setRecoveryStage(RECOVERY_STAGES.IDLE);
            setRecoveryResult(RECOVERY_RESULTS.NO_PROCESSORS);
            return;
        }

        setTimeout(() => {
            setRecoveryStage(RECOVERY_STAGES.IDLE);
            setRecoveryResult(RECOVERY_RESULTS.SUCCESS);
        }, 4000);
    }

    async function fetchDevicesOnHubNetwork() {
        const promises = [];
        hubs.forEach(hub => {
            var hubHasTcpProcessors = tcpProcessors.some(p => p.hubId === hub.hubId);
            if (!hubHasTcpProcessors) return;
            promises.push(runScan(hub));
        })
        return await Promise.all(promises); 
    }

    function filterScanResults(scanResults) {
        const mergedResults = [].concat(...scanResults);
        const uniqueDevices = mergedResults.filter((result, index, self) =>
            index === self.findIndex((r) => r.macAddress === result.macAddress)
        );
        return uniqueDevices;
    }

    function filterExistingProcessors(deviceList) {
        return deviceList.filter(device => {
            return processors.some(processor => {
                return processor.macAddress === device.macAddress;
            });
        });
        
    }   

    async function runScan(hub) {
        try {
            const res = await serverConnection.hubEventHandler.sendEvent(
                hub.hubId,
                hub.customerId,
                'SCAN_FOR_DEVICES',
                { protocol: null, subnet: null },
                60000
            );
            return res;
        } catch (err) {
            console.error('Error scanning for devices:', err);
            setRecoveryStage(RECOVERY_STAGES.IDLE);
            setRecoveryResult(RECOVERY_RESULTS.FAILED);
            return null;
        }
        
    }

    async function updateProcessorIp(foundProcessor, existingProcessor) {

        const changes = {
            processorId: existingProcessor.processorId,
            ipAddress: foundProcessor.ip,
        }
        const result = updateProcessors([changes]);
        console.log('Update result:', result);
    }
        
    return (
        <ModalContainer>
            <Frame>
                <ModalHeader>Processor Recovery</ModalHeader>
                <Button disabled={disabled} primary onClick={() => startProcessorRecovery()}>Start Processor Recovery</Button>
                    <ResultContainer>

                        {!tcpProcessors?.length && 
                            <CenterContainer>
                                <ModalText>You have no processors that can be recovered with this tool.</ModalText>
                                <Button tertiary onClick={onClose}>Close</Button>
                            </CenterContainer>
                        }

                        {recoveryStage === RECOVERY_STAGES.IDLE && recoveryResult === RECOVERY_RESULTS.NONE &&
                                <ModalText >
                                    No results yet.<br/><br/>
                                    Please start the recovery process.
                                </ModalText>
                        }

                        {recoveryStage === RECOVERY_STAGES.FETCHING &&
                            <CenterContainer>
                                <ModalText>
                                    This may take a minute.<br/>
                                    Please wait<br/><br/>
                                    Scanning Network...
                                </ModalText>
                                <LoadingSpinner/>
                            </CenterContainer>
                        }

                        {recoveryStage === RECOVERY_STAGES.PROCESSING &&
                            <CenterContainer>
                                <ModalText style={{ textAlign: 'center' }}>
                                    {fetchedDevices && `Found ${fetchedDevices.length} devices`}<br/>
                                    {foundProcessors && `Found ${foundProcessors.length} processors`}<br/><br/>
                                    Processing...
                                </ModalText>
                                <LoadingSpinner color={theme.colors.greenEnergy}/>
                            </CenterContainer>
                        }

                        {recoveryResult === RECOVERY_RESULTS.SUCCESS && <>
                            <label>Network scan complete!</label>
                            <label> Found {fetchedDevices.length} devices and {foundProcessors.length} audio processors</label>
                            <h3 style={{marginBottom: '0px'}}>Your Processors</h3>
                            <Frame style={{padding: '10px', gap: '10px'}}>
                                {foundProcessors?.map(foundProcessor=> {
                                    const matchedProcessor = processors.find(p => p.macAddress === foundProcessor.macAddress);
                                    return <ResultCard>
                                        <LineResultContainer style={{fontSize: '1.1rem', fontWeight: 'bold'}}>
                                            <label>Found:</label>
                                            <label>{matchedProcessor.type}</label>
                                        </LineResultContainer>

                                        <LineResultContainer>
                                            <ModalText>MAC:</ModalText>
                                            <ModalText>{matchedProcessor.macAddress}</ModalText>
                                        </LineResultContainer>

                                        <LineResultContainer/>

                                        <LineResultContainer>
                                            <ModalText>Confirmed IP:</ModalText>
                                            {matchedProcessor.ipAddress === foundProcessor.ip ?
                                                <ModalText>{foundProcessor.ip}</ModalText>
                                            : <ModalText >{foundProcessor.ip}</ModalText>}
                                        </LineResultContainer>

                                        
                                        {matchedProcessor.ipAddress === foundProcessor.ip ? (
                                            <LineResultContainer>
                                                <ModalText style={{color: theme.colors.greenEnergy}}>Registered IP</ModalText>
                                                <ModalText style={{color: theme.colors.greenEnergy}}>{matchedProcessor.ipAddress}</ModalText>
                                            </LineResultContainer>
                                        ) : (
                                            <LineResultContainer>
                                                <ModalText style={{color: theme.colors.raspberryRed}}>Registered IP</ModalText>
                                                <ModalText style={{color: theme.colors.raspberryRed}}>{matchedProcessor.ipAddress}</ModalText>
                                            </LineResultContainer>
                                        )}
                                        
                                        {matchedProcessor.ipAddress !== foundProcessor.ip && <>
                                            <LineResultContainer>
                                                <ModalText style={{color: theme.colors.raspberryRed}}>WARNING:</ModalText>
                                                <ModalText style={{color: theme.colors.raspberryRed}}>IP MISSMATCH</ModalText>
                                            </LineResultContainer>
                                            <Button primary style={{ margin: '10px 30px', backgroundColor: theme.colors.greenEnergy}} onClick={() => updateProcessorIp(foundProcessor, matchedProcessor)} >Recover Now!</Button>
                                        </>}
                                    </ResultCard>
                                })}
                            </Frame>
                        </>}

                        {recoveryResult === RECOVERY_RESULTS.EMPTY &&
                                <ModalText >
                                    No devices found on the network.<br/><br/>
                                    Please check your network connection and try again.
                                </ModalText>
                        }

                        {recoveryResult === RECOVERY_RESULTS.NO_PROCESSORS &&
                                <ModalText >
                                    No processors found on the network.<br/><br/>
                                    Please check your network connection and try again.
                                </ModalText>
                        }

                        {recoveryResult === RECOVERY_RESULTS.FAILED &&
                                <ModalText >
                                    An error occurred while scanning for devices.<br/><br/>
                                    Please check your network connection and try again.
                                </ModalText>
                        }

                        <Button style={{marginTop: 'auto' }} tertiary onClick={onClose}>Close</Button>
                    </ResultContainer>


            </Frame>
        </ModalContainer>
    )
};

export default ProcessorRecovery;